import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authService } from '../services/api';
import wsService from '../services/websocket';
import { apiErrorMessage } from '../utils/apiErrorMessage';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const sessionVersion = useRef(0);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const clearSession = useCallback(() => {
    authService.logout();
    setUser(null);
    wsService.disconnect();
    queryClient.clear();
  }, [queryClient]);

  const checkAuth = useCallback(async () => {
    const version = ++sessionVersion.current;
    queryClient.clear();
    wsService.disconnect();
    setUser(null);
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        const userData = await authService.getCurrentUser();
        if (version === sessionVersion.current) setUser(userData);
      }
    } catch (error) {
      if (version === sessionVersion.current) {
        clearSession();
      }
    } finally {
      if (version === sessionVersion.current) setLoading(false);
    }
  }, [queryClient, clearSession]);

  const invalidateSession = useCallback(() => {
    ++sessionVersion.current;
    wsService.disconnect();
  }, []);

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
    const changed = event => {
      if (event.key === 'access_token' || event.key === null) checkAuth();
    };
    window.addEventListener('storage', changed);
    return () => {
      invalidateSession();
      window.removeEventListener('storage', changed);
    };
  }, [checkAuth, invalidateSession]);

  // Connect WebSocket when user logs in
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (user && token) {
      wsService.connect(token);
    } else {
      wsService.disconnect();
    }
  }, [user]);

  const register = async (userData) => {
    try {
      setError(null);
      const response = await authService.register(userData);
      return { success: true, data: response };
    } catch (error) {
      const message = apiErrorMessage(error, 'Registration failed');
      setError(message);
      return { success: false, error: message };
    }
  };

  const login = async (username, password) => {
    const version = ++sessionVersion.current;
    clearSession();
    setLoading(true);
    try {
      setError(null);
      const response = await authService.login(username, password);
      if (version !== sessionVersion.current) return { success: false, error: 'Sign-in was cancelled.' };
      if (typeof response?.access_token !== 'string' || !response.access_token) throw new Error('Missing access token');
      localStorage.setItem('access_token', response.access_token);
      const userData = await authService.getCurrentUser();
      if (version !== sessionVersion.current) return { success: false, error: 'Sign-in was cancelled.' };
      setUser(userData);
      return { success: true };
    } catch (error) {
      const message = apiErrorMessage(error, 'Login failed');
      if (version === sessionVersion.current) {
        clearSession();
        setError(message);
      }
      return { success: false, error: message };
    } finally {
      if (version === sessionVersion.current) setLoading(false);
    }
  };

  const logout = () => {
    ++sessionVersion.current;
    clearSession();
    setLoading(false);
    setError(null);
  };

  const updateProfile = async (userData) => {
    const version = sessionVersion.current;
    try {
      setError(null);
      const updatedUser = await authService.updateUser(userData);
      if (version !== sessionVersion.current) return { success: false, error: 'Your session changed.' };
      setUser(updatedUser);
      return { success: true, data: updatedUser };
    } catch (error) {
      const message = apiErrorMessage(error, 'Profile update failed');
      if (version === sessionVersion.current) setError(message);
      return { success: false, error: message };
    }
  };

  const refreshUser = async () => {
    const version = sessionVersion.current;
    const token = localStorage.getItem('access_token');
    if (!token || !user) throw new Error('Your session changed.');
    const updatedUser = await authService.getCurrentUser();
    if (version !== sessionVersion.current || token !== localStorage.getItem('access_token') || updatedUser.id !== user.id) {
      throw new Error('Your session changed.');
    }
    setUser(updatedUser);
    return updatedUser;
  };

  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    updateProfile,
    checkAuth,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
